/**
 * Jobs section (U12/U13, approved mockup rev 6): two sub-lists —
 * **Workflows** (definitions) and **Schedules** (on/off timed runs).
 * Menu-less rows: a row is ONE button (click anywhere → the workflow's
 * Details page); the only inline controls are Run ▸ on workflow rows and
 * the On/Off pill on schedule rows (both stopPropagation). Download /
 * Delete / Restore live exclusively on the Details page. The FR-SCHED-4
 * cap conflict renders inline as the deactivate-one chooser copy.
 */

import { useState } from 'react';

import type { ImageJob } from '../../agents/imageJobsApi.ts';
import type { JobDefinition } from '../../agents/jobsApi.ts';
import { describeScheduleText } from '../../agents/scheduleText.ts';
import type { WorkflowSummary } from '../../agents/workflowsApi.ts';
import ImageFailureDialog from './ImageFailureDialog.tsx';
import type { CapConflict } from './useJobsData.ts';
import './JobsPanel.css';

interface JobsPanelProps {
  workflows: readonly WorkflowSummary[];
  schedules: readonly JobDefinition[];
  /** Image-generation jobs (C7). The Images sub-list renders only when at
   *  least one exists — non-image users never see the extra section. */
  imageJobs?: readonly ImageJob[];
  loading: boolean;
  error: string | null;
  capConflict: CapConflict | null;
  /** Row click — open the workflow's Details page (schedules open theirs
   *  scrolled to run history). */
  onOpenWorkflow: (workflowId: string, scrollToRuns?: boolean) => void;
  /** Run ▸ — hand off to Vera in chat (confirms cost conversationally). */
  onRunWorkflow: (workflow: WorkflowSummary) => void;
  onToggleSchedule: (jobId: string, enabled: boolean) => void;
  /** Completed image-job row click — open the delivered file in the Files
   *  preview. Absent → completed rows render inert like queued/failed ones. */
  onOpenImageFile?: (path: string) => void;
}

/** Display title for an image job: the prompt's opening words, never a raw
 *  id or filename. Word-boundary truncation; dated fallback for legacy
 *  records that predate prompt capture. */
function imageJobTitle(job: ImageJob): string {
  const prompt = job.prompt?.trim();
  if (prompt !== undefined && prompt.length > 0) {
    if (prompt.length <= 64) {
      return prompt;
    }
    const cut = prompt.slice(0, 64);
    const lastSpace = cut.lastIndexOf(' ');
    return `${cut.slice(0, lastSpace > 32 ? lastSpace : 64)}…`;
  }
  const created = job.createdAt !== undefined ? Date.parse(job.createdAt) : NaN;
  if (Number.isFinite(created)) {
    const day = new Date(created).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    return `Generated image · ${day}`;
  }
  return 'Generated image';
}

/** True when a temporary image's auto-delete instant has passed. */
function imageJobExpired(job: ImageJob): boolean {
  if (job.expiresAt === undefined) {
    return false;
  }
  const at = Date.parse(job.expiresAt);
  return Number.isFinite(at) && at <= Date.now();
}

/** "~2m left" / "~45s left" from the record's server-stamped ETA; null
 *  without a usable estimate or once it has elapsed. */
function imageJobEtaLabel(job: ImageJob): string | null {
  if (job.estimatedReadyAt === undefined) {
    return null;
  }
  const remainingSec = (Date.parse(job.estimatedReadyAt) - Date.now()) / 1000;
  if (!Number.isFinite(remainingSec) || remainingSec <= 0) {
    return null;
  }
  if (remainingSec < 60) {
    return `~${String(Math.ceil(remainingSec))}s left`;
  }
  return `~${String(Math.ceil(remainingSec / 60))}m left`;
}

/**
 * A schedule row's meta line: when it runs and when it next fires, in plain
 * language. A raw cron expression is never shown — that was the bug.
 */
function scheduleMeta(job: JobDefinition): string {
  const { summary, next } = describeScheduleText(job, new Date());
  // A disabled schedule has no next run to promise.
  return next === null || !job.enabled ? summary : `${summary} · ${next}`;
}

/** Copy for an image job's status line (expiry-aware for complete jobs). */
function imageJobStatus(job: ImageJob): string {
  if (job.status === 'queued') {
    const etaLabel = imageJobEtaLabel(job);
    return etaLabel === null ? 'Generating…' : `Generating… ${etaLabel}`;
  }
  if (job.status === 'complete') {
    if (imageJobExpired(job)) {
      return 'Expired';
    }
    if (job.kept === true) {
      return 'Ready · saved';
    }
    if (job.expiresAt !== undefined) {
      const days = Math.max(1, Math.ceil((Date.parse(job.expiresAt) - Date.now()) / 86_400_000));
      return `Ready · expires in ${days}d`;
    }
    return 'Ready';
  }
  if (job.status === 'failed') {
    return 'Failed';
  }
  return job.status;
}

export default function JobsPanel({
  workflows,
  schedules,
  imageJobs = [],
  loading,
  error,
  capConflict,
  onOpenWorkflow,
  onRunWorkflow,
  onToggleSchedule,
  onOpenImageFile,
}: JobsPanelProps) {
  const [failedJob, setFailedJob] = useState<ImageJob | null>(null);

  if (loading) {
    return <p className="om-jobs__empty">Loading…</p>;
  }
  if (error !== null) {
    return <p className="om-jobs__empty om-jobs__empty--error">{error}</p>;
  }

  const activeCount = schedules.filter((s) => s.enabled).length;

  // Expired records are deleted server-side by the reaper's periodic sweep;
  // this filter closes the window between an image's expiry and the next pass
  // so a dead row is never rendered. Expired means gone, not greyed out.
  const visibleImages = imageJobs.filter((job) => !imageJobExpired(job));

  /** A schedule's row title: the workflow's name, never the raw jobId. */
  const scheduleTitle = (job: JobDefinition): string =>
    workflows.find((w) => w.workflowId === job.workflowId)?.displayName ?? job.jobId;

  return (
    <div className="om-jobs">
      <div className="om-jobs__subhead">Workflows</div>
      {workflows.length === 0 ? (
        <p className="om-jobs__empty">
          No workflows yet — ask Vera to create one (e.g. “create a workflow that checks a
          stock’s fundamentals”).
        </p>
      ) : (
        workflows.map((wf) => (
          <button
            key={wf.workflowId}
            type="button"
            className="om-jobs__row"
            title="Open details"
            onClick={() => onOpenWorkflow(wf.workflowId)}
          >
            <span className="om-jobs__icon" aria-hidden="true">
              ⛁
            </span>
            <span className="om-jobs__main">
              <span className="om-jobs__name">{wf.displayName}</span>
              <span className="om-jobs__meta">
                v{wf.version} · {wf.stepCount} step{wf.stepCount === 1 ? '' : 's'}
              </span>
            </span>
            <span
              role="button"
              tabIndex={0}
              className="om-jobs__run"
              onClick={(e) => {
                e.stopPropagation();
                onRunWorkflow(wf);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  onRunWorkflow(wf);
                }
              }}
            >
              Run ▸
            </span>
          </button>
        ))
      )}

      <div className="om-jobs__subhead">
        Schedules{' '}
        {schedules.length > 0 ? (
          <span className="om-jobs__subcount">
            ({activeCount} of 2 active)
          </span>
        ) : null}
      </div>
      {schedules.length === 0 ? (
        <p className="om-jobs__empty">
          Nothing scheduled — ask Vera to schedule a workflow, or use “Schedule instead…”
          when running one.
        </p>
      ) : (
        schedules.map((job) => (
          <button
            key={job.jobId}
            type="button"
            className="om-jobs__row"
            data-off={!job.enabled}
            title="Open run history"
            onClick={() => onOpenWorkflow(job.workflowId, true)}
          >
            <span className="om-jobs__icon" aria-hidden="true">
              ◷
            </span>
            <span className="om-jobs__main">
              <span className="om-jobs__name">{scheduleTitle(job)}</span>
              <span className="om-jobs__meta">
                {scheduleMeta(job)}
                {job.params !== undefined
                  ? ` · ${Object.entries(job.params)
                      .map(([k, v]) => `${k}=${v}`)
                      .join(' ')}`
                  : ''}
              </span>
            </span>
            <span
              role="switch"
              aria-checked={job.enabled}
              tabIndex={0}
              className={`om-jobs__pill${job.enabled ? ' om-jobs__pill--on' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSchedule(job.jobId, !job.enabled);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleSchedule(job.jobId, !job.enabled);
                }
              }}
            >
              {job.enabled ? 'ON' : 'OFF'}
            </span>
          </button>
        ))
      )}

      {visibleImages.length > 0 ? (
        <>
          <div className="om-jobs__subhead">Images</div>
          {visibleImages.map((job) => {
            const firstFile = job.files?.[0];
            const openable =
              job.status === 'complete' && firstFile !== undefined && onOpenImageFile !== undefined;
            const failed = job.status === 'failed';
            const title = imageJobTitle(job);
            const body = (
              <>
                {job.thumbUrl !== undefined ? (
                  <img className="om-jobs__thumb" src={job.thumbUrl} alt={title} loading="lazy" />
                ) : (
                  <span className="om-jobs__icon" aria-hidden="true">
                    ▣
                  </span>
                )}
                <span className="om-jobs__main">
                  <span className="om-jobs__name" title={job.prompt ?? job.jobId}>
                    {title}
                  </span>
                  <span className="om-jobs__meta">
                    <span className="om-jobs__status" data-status={job.status}>
                      {imageJobStatus(job)}
                    </span>
                    {/* The failure REASON never inlines here — it is full prose
                        and wrecked the row on a phone. It lives in the dialog. */}
                    {failed ? ' · why?' : ''}
                    {openable ? ' · click to view' : ''}
                  </span>
                </span>
              </>
            );
            if (openable) {
              return (
                <button
                  key={job.jobId}
                  type="button"
                  className="om-jobs__row"
                  title="Open the image"
                  onClick={() => onOpenImageFile(`studio/${job.jobId}/${firstFile}`)}
                >
                  {body}
                </button>
              );
            }
            if (failed) {
              return (
                <button
                  key={job.jobId}
                  type="button"
                  className="om-jobs__row"
                  title="See why this failed"
                  onClick={() => setFailedJob(job)}
                >
                  {body}
                </button>
              );
            }
            return (
              <div key={job.jobId} className="om-jobs__row om-jobs__row--static">
                {body}
              </div>
            );
          })}
        </>
      ) : null}

      <ImageFailureDialog job={failedJob} onClose={() => setFailedJob(null)} />

      {capConflict !== null ? (
        <p className="om-jobs__cap" role="alert">
          {capConflict.message}
        </p>
      ) : null}
    </div>
  );
}
